import { supabase } from '../supabaseClient';

export const RATE_LIMITS = {
  chatbot:      { free: 15, pro: 25 },
  valuation:    { free: 5,  pro: 15 },
  neighbourhood:{ free: 0,  pro: Infinity },
  renovation:   { free: 8,  pro: 8 },
  translation:  { free: 30, pro: 30 },
};

export const WINDOW_HOURS = 3;
const WINDOW_MS = WINDOW_HOURS * 60 * 60 * 1000;

export async function checkRateLimit(feature, userId) {
  if (!userId) {
    return { allowed: false, limit: 0, used: 0, isPro: false, minutesUntilReset: 0, reason: 'not_logged_in' };
  }

  const config = RATE_LIMITS[feature];
  if (!config) {
    console.warn(`checkRateLimit: unknown feature "${feature}"`);
    return { allowed: true, limit: Infinity, used: 0, isPro: false, minutesUntilReset: 0 };
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('subscription_status, is_pro')
    .eq('id', userId)
    .single();

  const isPro =
    profile?.subscription_status === 'pro' ||
    profile?.subscription_status === 'active_pro' ||
    profile?.is_pro === true;

  const limit = isPro ? config.pro : config.free;

  if (limit === 0) {
    return { allowed: false, limit: 0, used: 0, isPro, minutesUntilReset: 0, reason: 'pro_required' };
  }

  if (limit === Infinity) {
    return { allowed: true, limit: Infinity, used: 0, isPro, minutesUntilReset: 0 };
  }

  const windowAgo = new Date(Date.now() - WINDOW_MS).toISOString();

  const { count } = await supabase
    .from('ai_call_logs')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('feature', feature)
    .eq('blocked', false)
    .gte('created_at', windowAgo);

  const used = count || 0;

  if (used >= limit) {
    const { data: oldest } = await supabase
      .from('ai_call_logs')
      .select('created_at')
      .eq('user_id', userId)
      .eq('feature', feature)
      .eq('blocked', false)
      .gte('created_at', windowAgo)
      .order('created_at', { ascending: true })
      .limit(1)
      .single();

    const resetAt = oldest
      ? new Date(oldest.created_at).getTime() + WINDOW_MS
      : Date.now() + WINDOW_MS;
    const minutesUntilReset = Math.max(1, Math.ceil((resetAt - Date.now()) / 60000));

    return { allowed: false, limit, used, isPro, minutesUntilReset, reason: 'limit_reached' };
  }

  return { allowed: true, limit, used, isPro, minutesUntilReset: 0 };
}

export async function logAICall(feature, userId, options = {}) {
  if (!userId) return;
  const { model = null, inputTokens = null, outputTokens = null, costUsd = null, blocked = false, blockReason = null } = options;
  try {
    await supabase.from('ai_call_logs').insert({
      user_id: userId,
      feature,
      model,
      input_tokens: inputTokens,
      output_tokens: outputTokens,
      cost_usd: costUsd,
      blocked,
      block_reason: blockReason,
    });
  } catch {
    // ai_call_logs may not exist yet — fail silently
  }
}

export function formatTimeUntilReset(minutes) {
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours} hour${hours === 1 ? '' : 's'}`;
  return `${hours}h ${mins}m`;
}
